import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, Box, Grid, MenuItem, TextField } from '@mui/material';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import { api } from 'common';
import { C2, C2_FONT } from '../components/Table/C2UserTable';
import {
  DemoModeAlert,
  SettingToggle,
  SettingsCard,
  SettingsField,
  SettingsNotice,
  SettingsSaveBar,
  isUrl,
  settingsFieldSx,
} from '../components/settings/C2SettingsKit';

const EMPTY = { apiUrl: '', method: 'POST', authorization: '', contentType: 'application/json', body: '' };
const METHODS = ['POST', 'GET', 'PUT'];
const CONTENT_TYPES = ['application/json', 'application/x-www-form-urlencoded', 'text/plain'];

const validate = (d, enabled) => {
  const e = {};
  if (!enabled) return e;
  if (!isUrl(d.apiUrl)) e.apiUrl = 'Escribe la URL completa del proveedor (https://…).';
  if (!METHODS.includes(String(d.method || '').toUpperCase())) e.method = 'Elige un método.';
  if (String(d.method).toUpperCase() === 'GET' && String(d.body || '').trim()) e.body = 'Con GET el cuerpo debe quedar vacío; pon los datos en la URL.';
  if (!/\{otp\}/i.test(`${d.apiUrl} ${d.body}`)) e.body = e.body || 'Incluye {otp} en la URL o en el cuerpo para enviar el código.';
  if (!/\{mobile\}/i.test(`${d.apiUrl} ${d.body}`)) e.apiUrl = e.apiUrl || 'Incluye {mobile} en la URL o en el cuerpo para indicar el destinatario.';
  return e;
};

const Token = ({ children }) => (
  <Box component="code" sx={{ px: 0.75, py: 0.2, borderRadius: '6px', backgroundColor: C2.lavender, color: C2.violet, fontWeight: 700 }}>
    {children}
  </Box>
);

const SmsSettings = ({ onDirtyChange }) => {
  const { editSmsConfig, editSettings } = api;
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const smsDetails = useSelector((state) => state.smsconfigdata.smsDetails);
  const canEdit = !!settings?.AllowCriticalEditsAdmin;
  const source = useMemo(() => ({ ...EMPTY, ...(smsDetails || {}) }), [smsDetails]);
  const sourceOtp = !!settings?.customMobileOTP;
  const [data, setData] = useState(source);
  const [customMobileOTP, setCustomMobileOTP] = useState(sourceOtp);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => { setData(source); }, [source]);
  useEffect(() => { setCustomMobileOTP(sourceOtp); }, [sourceOtp]);

  const configDirty = JSON.stringify(data) !== JSON.stringify(source);
  const otpDirty = customMobileOTP !== sourceOtp;
  const dirty = configDirty || otpDirty;
  const errors = useMemo(() => validate(data, customMobileOTP), [data, customMobileOTP]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const setField = (name, value) => setData((d) => ({ ...d, [name]: value }));

  const handleSave = async () => {
    if (!canEdit) {
      setNotice({ severity: 'info', message: 'Modo demostración: los cambios no se guardan.' });
      return;
    }
    if (Object.keys(errors).length) {
      setNotice({ severity: 'error', message: 'Revisa los campos marcados en rojo antes de activar el OTP propio.' });
      return;
    }
    setSaving(true);
    try {
      if (configDirty) {
        await dispatch(editSmsConfig({
          apiUrl: data.apiUrl.trim(),
          method: String(data.method).toUpperCase(),
          authorization: data.authorization.trim(),
          contentType: data.contentType.trim(),
          body: data.body,
        }));
      }
      if (otpDirty) await dispatch(editSettings({ customMobileOTP }));
      setNotice({ severity: 'success', message: 'Configuración de SMS guardada.' });
    } catch (e) {
      setNotice({ severity: 'error', message: `No se pudieron guardar los cambios: ${e?.message || e}` });
    } finally {
      setSaving(false);
    }
  };

  const hidden = !canEdit;
  const contentTypes = CONTENT_TYPES.includes(data.contentType) || !data.contentType ? CONTENT_TYPES : [...CONTENT_TYPES, data.contentType];

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      {!canEdit ? <DemoModeAlert /> : null}
      <SettingsNotice notice={notice} onClose={() => setNotice(null)} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <SettingsCard icon={SmsOutlinedIcon} title="Código de verificación" subtitle="Cómo reciben el código quienes entran con su número de celular.">
            <SettingToggle
              name="customMobileOTP"
              label="Usar mi proveedor de SMS"
              description="Si está apagado, Firebase envía el código. Si está encendido, se usa la API configurada aquí."
              checked={customMobileOTP}
              onChange={(name, v) => setCustomMobileOTP(v)}
              disabled={hidden}
            />
            <Alert severity="info" icon={false} sx={{ mt: 1.5, borderRadius: '12px', fontFamily: C2_FONT, lineHeight: 1.7 }}>
              En la URL o el cuerpo puedes usar <Token>{'{mobile}'}</Token> para el número del destinatario y <Token>{'{otp}'}</Token> para el código. La app los reemplaza al enviar cada SMS.
            </Alert>
          </SettingsCard>
        </Grid>
        <Grid item xs={12} md={7}>
          <SettingsCard icon={ApiOutlinedIcon} title="API del proveedor" subtitle="Datos de la petición HTTP que envía el SMS.">
            <SettingsField
              name="apiUrl"
              label="URL de la API"
              value={hidden ? 'Oculto en modo demo' : data.apiUrl}
              onChange={setField}
              error={errors.apiUrl}
              disabled={hidden}
              placeholder="https://api.proveedor.com/sms?to={mobile}"
            />
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  margin="dense"
                  label="Método"
                  value={String(data.method || '').toUpperCase()}
                  onChange={(e) => setField('method', e.target.value)}
                  error={!!errors.method}
                  helperText={errors.method || ' '}
                  disabled={hidden}
                  sx={settingsFieldSx}
                >
                  {METHODS.map((m) => <MenuItem key={m} value={m} sx={{ fontFamily: C2_FONT }}>{m}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  select
                  fullWidth
                  margin="dense"
                  label="Tipo de contenido"
                  value={data.contentType || ''}
                  onChange={(e) => setField('contentType', e.target.value)}
                  helperText=" "
                  disabled={hidden}
                  sx={settingsFieldSx}
                >
                  {contentTypes.map((c) => <MenuItem key={c} value={c} sx={{ fontFamily: C2_FONT }}>{c}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <SettingsField
              name="authorization"
              label="Encabezado Authorization"
              value={hidden ? 'Oculto' : data.authorization}
              onChange={setField}
              disabled={hidden}
              placeholder="Bearer tu_token"
              helper="Opcional. Se envía tal cual en el encabezado Authorization."
              autoComplete="off"
            />
            <SettingsField
              name="body"
              label="Cuerpo de la petición"
              value={hidden ? 'Oculto' : data.body}
              onChange={setField}
              error={errors.body}
              disabled={hidden}
              multiline
              minRows={4}
              placeholder={'{"to": "{mobile}", "message": "Tu código Conecta2 es {otp}"}'}
              sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />
          </SettingsCard>
        </Grid>
      </Grid>
      <SettingsSaveBar
        dirty={dirty}
        saving={saving}
        onSave={handleSave}
        onDiscard={() => { setData(source); setCustomMobileOTP(sourceOtp); }}
        disabled={!canEdit}
      />
    </form>
  );
};

export default SmsSettings;
